import { SelectOption } from '@globaleris/ui.library'
import moment from 'moment'
import { EnumClaseFactura, EnumCommodity, EnumEstadoFactura, EnumTipoFactura } from '../../api'
import {
  InvoiceDetailResponseModel,
  InvoiceProductModel
} from '../../api/Argentia/endpoints/invoices/model/invoices'
import { SettlementTableModel } from '../../api/Argentia/endpoints/settlements/model/settlements'
import { EnumClientTypes } from '../../api/Argentia/enums/enumClientTypes'
import { ProductConceptModel } from '../../api/Argentia/model/contracts'
import { enumEnumToValueNumber, enumKeyToValue } from '../../helpers'
import { DateFilters, DatePeriod } from '../../model'
import { getActiveBalanceZone, getActiveCompany } from '../../redux'
import { InvoiceFormModel } from './components/invoiceForm/invoiceForm'
import { InvoicesTableConfig } from './components/invoicesTable/invoicesTable'
import {
  FilterDateBy,
  InvoiceFilterModel,
  InvoicesTableModel,
  ObjectionTableModel,
  TabsInvoicesType
} from './invoicesPage.Model'
import { canManual, getGlobalActionsConfiguration } from './invoicesPage.service'

export const listObjDescriptionsItemMapper = (items: any[]): SelectOption[] => {
  return items && items.map(objectionItemMapper)
}

export const objectionItemMapper = (item: ObjectionTableModel): SelectOption => {
  return (
    item && {
      value: item.idObjMaestra,
      label: item.description
    }
  )
}

export const invoicesItemMapper = (item: InvoicesTableModel): InvoicesTableModel => {
  const invoiceType = enumEnumToValueNumber(EnumTipoFactura, item.invoiceType)
  return (
    item && {
      ...item,
      canManual: canManual(item),
      invoiceType: invoiceType,
      showDeliveryNotes: invoiceType === EnumTipoFactura.Recapitulativa ? true : false,
      showAggregatedInvoice:
        invoiceType === EnumTipoFactura.Generada && item.aggregatedInvoiceId ? true : false,
      actionsConfiguration: getGlobalActionsConfiguration()
    }
  )
}

export const invoicesItemsMapper = (items: InvoicesTableModel[]): InvoicesTableModel[] => {
  return items && items.map(invoicesItemMapper)
}

export const invoiceFilterMapper = ({
  commodity,
  clientType,
  dateRange,
  invoiceCode,
  subjectCode,
  contractCode,
  cupsCode,
  activeDateFilter,
  filterDateBy,
  invoiceStates,
  activePageTab,
  subjectId,
  hasObjections
}: {
  clientType: EnumClientTypes
  dateRange: DatePeriod
  invoiceCode: string
  subjectCode: string
  filterDateBy: FilterDateBy
  contractCode?: string
  cupsCode?: string
  commodity?: EnumCommodity
  activeDateFilter?: DateFilters
  invoiceStates?: EnumEstadoFactura[] | null
  activePageTab?: TabsInvoicesType | null
  subjectId?: number
  hasObjections?: boolean | null
}): InvoiceFilterModel => {
  let invoiceType: EnumTipoFactura[] = []
  if (activePageTab) {
    switch (activePageTab) {
      case TabsInvoicesType.finalClient:
      case TabsInvoicesType.extendedClient:
        invoiceType = [EnumTipoFactura.Generada, EnumTipoFactura.Proforma, EnumTipoFactura.Manual]
        break
      case TabsInvoicesType.commercial:
      case TabsInvoicesType.wholesaler:
      case TabsInvoicesType.shipper:
        invoiceType = [EnumTipoFactura.Generada]
        break
      case TabsInvoicesType.distribution:
        invoiceType = [EnumTipoFactura.Generada, EnumTipoFactura.Recibida]
        break
      case TabsInvoicesType.aggregatedInvoice_finalClient:
      case TabsInvoicesType.aggregatedInvoice_extendedClient:
        invoiceType = [EnumTipoFactura.Recapitulativa, EnumTipoFactura.Resumen]
        break
    }
  } else {
    switch (clientType) {
      case EnumClientTypes.FINAL_CLIENT:
        invoiceType = [
          EnumTipoFactura.Generada,
          EnumTipoFactura.Proforma,
          EnumTipoFactura.Manual,
          EnumTipoFactura.Recapitulativa,
          EnumTipoFactura.Resumen
        ]
        break
      case EnumClientTypes.COMMERCIAL:
      case EnumClientTypes.WHOLESALER:
      case EnumClientTypes.SHIPPER:
        invoiceType = [EnumTipoFactura.Generada]
        break
      case EnumClientTypes.DISTRIBUTOR:
        invoiceType = [EnumTipoFactura.Generada, EnumTipoFactura.Recibida]
        break
    }
  }

  const activeBalanceZone = getActiveBalanceZone()?.id
  const activeCompany = getActiveCompany()?.id

  const parseStartDate = dateRange.start ? moment(dateRange.start).format('YYYY-MM-DD') : undefined
  const parseEndDate = dateRange.end ? moment(dateRange.end).format('YYYY-MM-DD') : undefined
  return {
    startDate: parseStartDate,
    endDate: parseEndDate,
    invoiceType: invoiceType,
    subjectType: clientType,
    balanceZone: activeBalanceZone!,
    companyID: activeCompany!,
    commodity: commodity,
    invoice: invoiceCode,
    subject: subjectCode,
    contractCode,
    cupsCode,
    filterDateBy,
    subjectId: subjectId,
    states: invoiceStates?.map(state => enumEnumToValueNumber(EnumEstadoFactura, state)!),
    hasObjections: hasObjections,
    lastRecords:
      activeDateFilter && activeDateFilter === DateFilters.RECORDS_LIM_500
        ? activeDateFilter
        : undefined
  }
}

export const mapTableConfiguration = (activeTabValue: number): InvoicesTableConfig => {
  let config: InvoicesTableConfig = {
    elevation: 1,
    rowsPerPage: 10,
    rowsPerPageOptions: undefined,
    queryInvoiceStates: null
  }
  switch (activeTabValue) {
    case TabsInvoicesType.finalClient:
      return (config = {
        ...config,
        hideTableColumns: ['subjectSource', 'settlementGroupCode']
      })
    case TabsInvoicesType.distribution:
      return (config = {
        ...config,
        hideTableColumns: ['contractCode', 'subjectSource', 'invoiceType', 'settlementGroupCode']
      })
    case TabsInvoicesType.commercial:
      return (config = {
        ...config,
        hideTableColumns: [
          'contractCode',
          'subjectSource',
          'cups',
          'invoiceType',
          'settlementGroupCode'
        ]
      })
    case TabsInvoicesType.wholesaler:
      return (config = {
        ...config,
        hideTableColumns: ['contractCode', 'cups', 'invoiceType', 'settlementGroupCode']
      })
    case TabsInvoicesType.shipper:
      return (config = {
        ...config,
        hideTableColumns: [
          'contractCode',
          'subjectSource',
          'cups',
          'invoiceType',
          'settlementGroupCode'
        ]
      })
    case TabsInvoicesType.aggregatedInvoice_finalClient:
    case TabsInvoicesType.aggregatedInvoice_extendedClient:
      return (config = {
        ...config,
        hideTableColumns: ['contractCode', 'subjectSource', 'cups']
      })
    default:
      return config
  }
}

export interface AnnullableInvoicesFilterResult {
  total: number
  annullableRows: InvoicesTableModel[] //Facturas anulables que pasan por el flujo de ciclo de anulación
  penaltyRows: InvoicesTableModel[] //Facturas de penalización manuales, enviadas directamente a GenerateCancellationConcepts
  annullableCount: number
  nonAnnullableCount: number
}

export interface ReinvoiceableInvoicesFilterResult {
  total: number
  reinvoiceableRows: InvoicesTableModel[] //Facturas que pasan por el flujo de ciclo de refacturación
  penaltyRows: InvoicesTableModel[] //Facturas de penalización manuales, enviadas directamente a GenerateCancellationConcepts
  reinvoiceableCount: number
  nonReinvoiceableCount: number
}

const CYCLE_ALLOWED_CLASSES = [
  EnumClaseFactura.Normal,
  EnumClaseFactura.AnuladoraConSustituyente,
  EnumClaseFactura.Complementaria,
  EnumClaseFactura.Regularizadora,
  EnumClaseFactura.Derechos,
  EnumClaseFactura.Penalizacion
]

const CYCLE_INVOICE_TYPES = [
  EnumTipoFactura.Generada,
  EnumTipoFactura.Proforma,
  EnumTipoFactura.Albaran,
  EnumTipoFactura.Recapitulativa,
  EnumTipoFactura.Resumen
]

const isIssuedInvoice = (row: InvoicesTableModel): boolean => {
  const statusValue = enumEnumToValueNumber(EnumEstadoFactura, row.status)
  return (
    statusValue != null &&
    [EnumEstadoFactura.Emitida, EnumEstadoFactura.Finalizada].includes(statusValue)
  )
}

export const isManualInvoice = (row: InvoicesTableModel): boolean =>
  enumEnumToValueNumber(EnumTipoFactura, row.invoiceType) === EnumTipoFactura.Manual

export const isManualPenaltyInvoice = (row: InvoicesTableModel): boolean =>
  isManualInvoice(row) &&
  enumEnumToValueNumber(EnumClaseFactura, row.class) === EnumClaseFactura.Penalizacion

const isCycleEligible = (row: InvoicesTableModel): boolean => {
  const typeValue = enumEnumToValueNumber(EnumTipoFactura, row.invoiceType)
  const classValue = enumEnumToValueNumber(EnumClaseFactura, row.class)
  return (
    isIssuedInvoice(row) &&
    typeValue != null &&
    CYCLE_INVOICE_TYPES.includes(typeValue) &&
    classValue != null &&
    CYCLE_ALLOWED_CLASSES.includes(classValue) &&
    row.emisionDate != null
  )
}

//Cualquier factura manual emitida puede ser anulada, independientemente de su clase
export const isAnnullableInvoice = (row: InvoicesTableModel): boolean =>
  isManualInvoice(row) ? isIssuedInvoice(row) : isCycleEligible(row)

//Solo las facturas manuales de penalización pueden ser refacturadas
export const isReinvoiceableInvoice = (row: InvoicesTableModel): boolean =>
  isManualPenaltyInvoice(row) ? isIssuedInvoice(row) : isCycleEligible(row)

/**
 * Divide las facturas seleccionadas para anulación:
 * - Facturas de penalización manuales (emitidas) -> penaltyRows
 * - Facturas manuales emitidas y facturas elegibles por ciclo -> annullableRows (flujo de ciclo)
 */

export const filterAnnullableInvoices = (
  selectedRows: InvoicesTableModel[]
): AnnullableInvoicesFilterResult => {
  const total = selectedRows.length

  const penaltyRows = selectedRows.filter(
    row => isManualPenaltyInvoice(row) && isIssuedInvoice(row)
  )
  const annullableRows = selectedRows.filter(
    row => !isManualPenaltyInvoice(row) && isAnnullableInvoice(row)
  )
  const annullableCount = annullableRows.length + penaltyRows.length

  return {
    total,
    annullableRows,
    penaltyRows,
    annullableCount,
    nonAnnullableCount: total - annullableCount
  }
}

/**
 * Divide las facturas seleccionadas para refacturación:
 * - Facturas de penalización manuales (emitidas) -> penaltyRows
 * - Facturas elegibles por ciclo -> reinvoiceableRows (flujo de ciclo)
 */
export const filterReinvoiceableInvoices = (
  selectedRows: InvoicesTableModel[]
): ReinvoiceableInvoicesFilterResult => {
  const total = selectedRows.length

  const penaltyRows = selectedRows.filter(
    row => isManualPenaltyInvoice(row) && isIssuedInvoice(row)
  )
  const reinvoiceableRows = selectedRows.filter(row => isCycleEligible(row))
  const reinvoiceableCount = reinvoiceableRows.length + penaltyRows.length

  return {
    total,
    reinvoiceableRows,
    penaltyRows,
    reinvoiceableCount,
    nonReinvoiceableCount: total - reinvoiceableCount
  }
}

export const mapSettlementTableApiResponse = (
  items: SettlementTableModel[]
): SettlementTableModel[] => {
  return items && items.map(mapSettlementTableServiceApiItem)
}
export const mapSettlementTableServiceApiItem = (
  item: SettlementTableModel
): SettlementTableModel => {
  return {
    ...item,
    actionsConfiguration: getGlobalActionsConfiguration()
  }
}

const deduplicateProductsByContractId = (
  products: InvoiceProductModel[]
): InvoiceProductModel[] => {
  const seen = new Set<number>()
  return products.filter(product => {
    if (seen.has(product.id)) return false
    seen.add(product.id)
    return true
  })
}

export const mapInvoiceDetailToFormModel = (
  detail: InvoiceDetailResponseModel
): InvoiceFormModel => ({
  id: detail.id,
  code: detail.code,
  subject: detail.subjectDetail?.name ?? detail.subject,
  nifSubject: detail.subjectDetail?.fiscalDocument ?? detail.nifSubject,
  subjectSource: detail.subjectSourceDetail?.name ?? detail.subjectSource,
  nifSubjectSource: detail.subjectSourceDetail?.fiscalDocument ?? detail.nifSubjectSource,
  class: enumKeyToValue(EnumClaseFactura, detail.class) as EnumClaseFactura,
  contractCode: detail.contractCode,
  cups: detail.cups,
  startDate: detail.startDate,
  endDate: detail.endDate,
  productIds: deduplicateProductsByContractId(detail.products ?? []).map(p => p.id)
})

export const mapProductsToSelectOptions = (products: InvoiceProductModel[]): SelectOption[] => {
  return deduplicateProductsByContractId(products).map(product => ({
    value: product.id,
    label: product.description
  }))
}

export const mapProductConceptsToSelectOptions = (
  productConcepts: ProductConceptModel[]
): SelectOption[] => {
  return productConcepts.map(concept => ({
    value: concept.conceptId,
    label: `${concept.code} - ${concept.description}`
  }))
}
